import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BudgetVsActualChart from '../components/charts/BudgetVsActualChart';
import OverallBudgetChart from '../components/charts/OverallBudgetChart';
import PeriodSelector from '../components/PeriodSelector';
import CostpointDetailView from '../components/reports/CostpointDetailView';
import FinancialOverviewCard from '../components/reports/FinancialOverviewCard';
import TagAnalysisSection from '../components/reports/TagAnalysisSection';
import useReportData from '../hooks/useReportData';
import { ReportsApiError, reportsApi } from '../services/reportsApi';
import type {
  CostpointTag,
  CostpointTransaction,
  PeriodFilterType,
  TagBasedAnalysis,
} from '../types/reporting.types';

const Reports: React.FC = () => {
  // State management
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodFilterType>('H1');
  const {
    reportData,
    isLoading,
    error: reportError,
    reload: loadReportData,
  } = useReportData(selectedYear, selectedPeriod);
  const [costpointError, setCostpointError] = useState<string | null>(null);
  const error = reportError ?? costpointError;
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedCostpoint, setSelectedCostpoint] = useState<string>('all');

  // New state for costpoint detail view
  const [costpointTags, setCostpointTags] = useState<CostpointTag[]>([]);
  const [costpointTransactions, setCostpointTransactions] = useState<CostpointTransaction[]>([]);
  const [isLoadingCostpointData, setIsLoadingCostpointData] = useState(false);
  const [selectedTagForTransactions, setSelectedTagForTransactions] = useState<string>('');

  // Load costpoint detail data (tags and transactions)
  const loadCostpointData = useCallback(
    async (costpoint: string) => {
      if (costpoint === 'all') {
        setCostpointTags([]);
        setCostpointTransactions([]);
        return;
      }

      setIsLoadingCostpointData(true);
      try {
        // Calculate date range based on selected period
        const startDate = reportData?.period.start_date || `${selectedYear}-01-01`;
        const endDate = reportData?.period.end_date || `${selectedYear}-12-31`;

        // Load tags and transactions in parallel
        const [tagsData, transactionsData] = await Promise.all([
          reportsApi.getCostpointTags(costpoint, startDate, endDate),
          reportsApi.getAllCostpointTransactions(costpoint, startDate, endDate),
        ]);

        setCostpointTags(tagsData as CostpointTag[]);
        setCostpointTransactions(transactionsData as CostpointTransaction[]);
      } catch (err) {
        if (err instanceof ReportsApiError) {
          setCostpointError(err.message);
        } else {
          setCostpointError('Failed to load costpoint data');
        }
      } finally {
        setIsLoadingCostpointData(false);
      }
    },
    [reportData, selectedYear]
  );

  // Load transactions filtered by tag
  const loadTransactionsByTag = useCallback(
    async (costpoint: string, tag: string) => {
      if (costpoint === 'all' || !tag) return;

      setIsLoadingCostpointData(true);
      try {
        const startDate = reportData?.period.start_date || `${selectedYear}-01-01`;
        const endDate = reportData?.period.end_date || `${selectedYear}-12-31`;

        const transactionsData = await reportsApi.getAllCostpointTransactions(
          costpoint,
          startDate,
          endDate,
          tag
        );

        setCostpointTransactions(transactionsData as CostpointTransaction[]);
      } catch (err) {
        if (err instanceof ReportsApiError) {
          setCostpointError(err.message);
        } else {
          setCostpointError('Failed to load filtered transactions');
        }
      } finally {
        setIsLoadingCostpointData(false);
      }
    },
    [reportData, selectedYear]
  );

  // Load costpoint data or tag-filtered transactions when costpoint/tag selection changes
  useEffect(() => {
    if (!reportData) return;
    if (selectedTagForTransactions && selectedCostpoint !== 'all') {
      loadTransactionsByTag(selectedCostpoint, selectedTagForTransactions);
    } else {
      loadCostpointData(selectedCostpoint);
    }
  }, [
    selectedCostpoint,
    selectedTagForTransactions,
    reportData,
    loadCostpointData,
    loadTransactionsByTag,
  ]);

  // Get available costpoints for filtering
  const availableCostpoints = React.useMemo(() => {
    if (!reportData) return [];

    return reportData.costpoint_reports.map((report) => ({
      value: report.costpoint,
      label: report.costpoint.toUpperCase(),
    }));
  }, [reportData]);

  // Get all unique tags for filtering (filtered by selected costpoint)
  const availableTags = React.useMemo(() => {
    if (!reportData) return [];

    const tagSet = new Set<string>();
    reportData.costpoint_reports.forEach((report) => {
      // Filter by selected costpoint if not "all"
      if (selectedCostpoint === 'all' || report.costpoint === selectedCostpoint) {
        report.tag_breakdown.forEach((tag) => {
          tagSet.add(tag.tag_name);
        });
      }
    });

    return Array.from(tagSet).sort();
  }, [reportData, selectedCostpoint]);

  // Filter tag breakdown by selected tags and costpoint
  const filteredTagBreakdown = useMemo(() => {
    if (!reportData || selectedTags.length === 0) return [];
    const allTags: TagBasedAnalysis[] = [];
    reportData.costpoint_reports.forEach((report) => {
      if (selectedCostpoint === 'all' || report.costpoint === selectedCostpoint) {
        report.tag_breakdown.forEach((tag) => {
          if (selectedTags.includes(tag.tag_name)) allTags.push(tag);
        });
      }
    });
    return allTags.sort((a, b) => a.tag_display_name.localeCompare(b.tag_display_name));
  }, [reportData, selectedTags, selectedCostpoint]);

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Financial Reports</h1>
        <p className="mt-2 text-sm text-gray-700">
          Budget vs actual analysis with detailed costpoint and tag breakdown
        </p>
      </div>

      {/* Period Selector */}
      <div className="mb-6">
        <PeriodSelector
          selectedYear={selectedYear}
          selectedPeriod={selectedPeriod}
          onYearChange={setSelectedYear}
          onPeriodChange={setSelectedPeriod}
          isLoading={isLoading}
        />
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading report</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={loadReportData}
                className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && !reportData && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading report data...</span>
        </div>
      )}

      {/* Report Content */}
      {reportData && (
        <>
          {/* Summary Cards */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
            {/* Combined Summary - Detailed Breakdown */}
            <FinancialOverviewCard combinedSummary={reportData.combined_summary} />

            {/* Overall Budget Chart */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <OverallBudgetChart
                  totalBudgetedIncome={reportData.combined_summary.total_budgeted_income}
                  totalActualIncome={reportData.combined_summary.total_actual_income}
                  totalBudgetedExpenses={reportData.combined_summary.total_budgeted_expenses}
                  totalActualExpenses={reportData.combined_summary.total_actual_expenses}
                  taxNet={reportData.combined_summary.tax_net}
                  actualCashFlow={reportData.combined_summary.actual_cash_flow}
                />
              </div>
            </div>
          </div>

          {/* Period Info */}
          <div className="mb-6 bg-gray-50 rounded-lg p-4">
            <div className="space-y-2 sm:space-y-0 sm:flex sm:flex-wrap sm:items-center sm:justify-between text-sm text-gray-600">
              <div className="space-y-2 sm:space-y-0 sm:flex sm:items-center sm:space-x-6">
                <div>
                  <span className="font-medium">Period:</span> {reportData.period.display_name}
                </div>
                <div>
                  <span className="font-medium">Date Range:</span>{' '}
                  <span className="block sm:inline">
                    {new Date(reportData.period.start_date).toLocaleDateString()} -{' '}
                    {new Date(reportData.period.end_date).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div>
                <span className="font-medium">Costpoints:</span>{' '}
                {reportData.costpoint_reports.length}
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="mb-6">
            <div className="bg-white overflow-hidden shadow rounded-lg p-4 sm:p-6">
              <BudgetVsActualChart data={reportData.costpoint_reports} />
            </div>
          </div>

          {/* Tag-Based Analysis */}
          {availableTags.length > 0 && (
            <TagAnalysisSection
              availableTags={availableTags}
              availableCostpoints={availableCostpoints}
              selectedCostpoint={selectedCostpoint}
              selectedTags={selectedTags}
              filteredTagBreakdown={filteredTagBreakdown}
              onCostpointChange={(costpoint) => {
                setSelectedCostpoint(costpoint);
                setSelectedTags([]);
              }}
              onTagToggle={(tag) => {
                if (selectedTags.includes(tag)) {
                  setSelectedTags(selectedTags.filter((t) => t !== tag));
                } else {
                  setSelectedTags([...selectedTags, tag]);
                }
              }}
            />
          )}

          {/* Costpoint Detail View */}
          {selectedCostpoint !== 'all' && (
            <CostpointDetailView
              selectedCostpoint={selectedCostpoint}
              costpointTags={costpointTags}
              costpointTransactions={costpointTransactions}
              isLoadingCostpointData={isLoadingCostpointData}
              selectedTagForTransactions={selectedTagForTransactions}
              onTagSelect={(tagName) => setSelectedTagForTransactions(tagName)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
